import { Checkbox, DatePicker, Input, InputNumber, Radio, Select, Space, Spin } from 'antd';
import RangeSliderField from './RangeSliderField.jsx';
import DateRangePresetField from './DateRangePresetField.jsx';

const { TextArea } = Input;
const { RangePicker } = DatePicker;

// Every renderer is called with the same props, so a new input type never has to
// re-implement option fetching, disabled handling or Form wiring:
//   { field, value, onChange, options, optionsLoading, disabled, placeholder }
//
// Registry entry flags:
//   booleanEligible — admins may mark fields of this type "Boolean capable"
//   needsOptions    — SearchFieldControl resolves field.dataSource before rendering
//   multiValue      — the control emits an array, mapped onto several API params
export const FIELD_TYPE_REGISTRY = {};

export function registerFieldType(type, definition) {
  if (!type || typeof definition?.render !== 'function') {
    throw new Error('registerFieldType(type, { render }) requires a type name and a render function');
  }
  FIELD_TYPE_REGISTRY[type] = {
    booleanEligible: false,
    needsOptions: false,
    multiValue: false,
    ...definition,
  };
  return FIELD_TYPE_REGISTRY[type];
}

export function getFieldType(type) {
  return FIELD_TYPE_REGISTRY[type] ?? FIELD_TYPE_REGISTRY.text;
}

export function isBooleanEligible(type) {
  return Boolean(getFieldType(type).booleanEligible);
}

export function getBooleanEligibleTypes() {
  return Object.entries(FIELD_TYPE_REGISTRY)
    .filter(([, definition]) => definition.booleanEligible)
    .map(([type]) => type);
}

function enterText(field) {
  return `Enter ${String(field.label ?? field.field ?? '').toLowerCase()}`;
}

function selectText(field) {
  return `Select ${String(field.label ?? field.field ?? '').toLowerCase()}`;
}

registerFieldType('text', {
  booleanEligible: true,
  render: ({ field, value, onChange, disabled, placeholder }) => (
    <Input
      value={value ?? ''}
      disabled={disabled}
      placeholder={placeholder ?? enterText(field)}
      allowClear
      onChange={(event) => onChange(event.target.value)}
    />
  ),
});

registerFieldType('email', {
  booleanEligible: true,
  render: ({ field, value, onChange, disabled, placeholder }) => (
    <Input
      type="email"
      value={value ?? ''}
      disabled={disabled}
      placeholder={placeholder ?? enterText(field)}
      allowClear
      onChange={(event) => onChange(event.target.value)}
    />
  ),
});

registerFieldType('textarea', {
  booleanEligible: true,
  render: ({ field, value, onChange, disabled, placeholder }) => (
    <TextArea
      rows={field.rows ?? 3}
      value={value ?? ''}
      disabled={disabled}
      placeholder={placeholder ?? enterText(field)}
      onChange={(event) => onChange(event.target.value)}
    />
  ),
});

registerFieldType('number', {
  render: ({ field, value, onChange, disabled, placeholder }) => (
    <InputNumber
      style={{ width: '100%' }}
      value={value ?? null}
      disabled={disabled}
      min={field.min}
      max={field.max}
      placeholder={placeholder ?? enterText(field)}
      onChange={onChange}
    />
  ),
});

registerFieldType('select', {
  booleanEligible: true,
  needsOptions: true,
  render: ({ field, value, onChange, options, optionsLoading, disabled, placeholder }) => (
    <Select
      mode={field.multiSelect || field.mode === 'multiple' ? 'multiple' : undefined}
      value={value ?? undefined}
      disabled={disabled}
      options={options}
      loading={optionsLoading}
      allowClear
      showSearch
      optionFilterProp="label"
      placeholder={placeholder ?? selectText(field)}
      notFoundContent={optionsLoading ? <Spin size="small" /> : undefined}
      onChange={onChange}
    />
  ),
});

registerFieldType('multiselect', {
  booleanEligible: true,
  needsOptions: true,
  multiValue: true,
  render: ({ field, value, onChange, options, optionsLoading, disabled, placeholder }) => (
    <Select
      mode="multiple"
      value={value ?? undefined}
      disabled={disabled}
      options={options}
      loading={optionsLoading}
      allowClear
      showSearch
      optionFilterProp="label"
      placeholder={placeholder ?? selectText(field)}
      notFoundContent={optionsLoading ? <Spin size="small" /> : undefined}
      onChange={onChange}
    />
  ),
});

// Live search configs use type:"location" for a single location picker, so it
// resolves against the location master unless the config says otherwise.
registerFieldType('location', {
  needsOptions: true,
  defaultDataSource: 'locationMaster',
  render: ({ field, value, onChange, options, optionsLoading, disabled, placeholder }) => (
    <Select
      value={value ?? undefined}
      disabled={disabled}
      options={options}
      loading={optionsLoading}
      allowClear
      showSearch
      optionFilterProp="label"
      placeholder={placeholder ?? selectText(field)}
      notFoundContent={optionsLoading ? <Spin size="small" /> : undefined}
      onChange={onChange}
    />
  ),
});

// Free-text multi-entry: type a value, press Enter, it becomes a chip. Boolean
// eligible because a skills-style field is the most common boolean target.
registerFieldType('tagify', {
  booleanEligible: true,
  multiValue: true,
  render: ({ field, value, onChange, disabled, placeholder }) => (
    <Select
      mode="tags"
      value={value ?? undefined}
      disabled={disabled}
      open={false}
      suffixIcon={null}
      allowClear
      tokenSeparators={[',']}
      placeholder={placeholder ?? enterText(field)}
      onChange={onChange}
    />
  ),
});

// A "locked" option (admin-configured, off by default — see FormGroupsSection
// Options/Values editor) is pre-selected and cannot be deselected, e.g.
// "Internal" always included in Search Preference. Radio only allows one
// selection at all, so a locked radio option fixes the whole group to it
// (disabled) rather than mixing "one fixed, others free" into a
// mutually-exclusive control where that has no real meaning.
registerFieldType('radio', {
  needsOptions: true,
  render: ({ value, onChange, options, disabled }) => {
    const locked = options.find((option) => option.locked);
    return (
      <Radio.Group
        value={locked ? locked.value : value}
        disabled={disabled || Boolean(locked)}
        options={options}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  },
});

// An "All" checkbox is automatic, not admin-authored: any checkbox field
// with more than one option gets one, unless the field explicitly opts out
// (field.hideSelectAll — FormGroupsSection's one field-level switch). It is
// never part of `options` and never enters the submitted value — its
// checked state is computed fresh every render from whether every real
// option is currently selected, so it can never visibly disagree with
// reality the way a stored trigger flag could. `locked` (per-option,
// admin-configured, unrelated to this) still composes for free, since the
// derived state reads from the already-locked-merged value.
registerFieldType('checkbox', {
  needsOptions: true,
  multiValue: true,
  render: ({ field, value, onChange, options, disabled }) => {
    if (options.length === 0) {
      return (
        <Checkbox
          checked={Boolean(value)}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
        >
          {field.checkboxLabel ?? field.label}
        </Checkbox>
      );
    }

    const lockedValues = options.filter((option) => option.locked).map((option) => option.value);
    const allValues = options.map((option) => option.value);
    const current = Array.isArray(value) ? value : [];

    // Every read and write goes through here, so the stored value is always in
    // the admin's configured option order. Downstream this value IS the
    // source-tab order (SearchBuilderV1 -> getSearchSources -> buildSourceTabs),
    // and the merges below concatenate lockedValues onto whatever came in — so a
    // locked value merged into a stored or seeded value could otherwise land out
    // of order and reorder the tab strip. antd already hands the group's value
    // back in options order, so this is a guarantee at the boundary rather than
    // a fix for a live bug; the tab reordering itself was buildSourceTabs.
    const orderByOptions = (values) => {
      const wanted = new Set(values);
      return allValues.filter((option) => wanted.has(option));
    };

    // Seeds locked values in even before any Form initialValue has resolved,
    // and guards against them ever being missing from what's displayed.
    const effectiveValue = orderByOptions([...current, ...lockedValues]);
    const groupOptions = options.map((option) => (
      option.locked ? { ...option, disabled: true } : option
    ));

    const handleGroupChange = (next) => {
      onChange(orderByOptions([...next, ...lockedValues]));
    };

    const showSelectAll = field.hideSelectAll !== true && options.length > 1;
    const allChecked = showSelectAll && allValues.every((v) => effectiveValue.includes(v));
    const handleSelectAllChange = (event) => {
      onChange(event.target.checked ? orderByOptions(allValues) : orderByOptions(lockedValues));
    };

    return (
      <Space direction="vertical" size={4} className="sbv1-checkbox-with-select-all">
        {showSelectAll && (
          <Checkbox checked={allChecked} disabled={disabled} onChange={handleSelectAllChange}>
            All
          </Checkbox>
        )}
        <Checkbox.Group
          value={effectiveValue}
          disabled={disabled}
          options={groupOptions}
          onChange={handleGroupChange}
        />
      </Space>
    );
  },
});

registerFieldType('date', {
  render: ({ value, onChange, disabled, placeholder }) => (
    <DatePicker
      style={{ width: '100%' }}
      value={value ?? null}
      disabled={disabled}
      placeholder={placeholder ?? 'Select date'}
      onChange={onChange}
    />
  ),
});

registerFieldType('daterange', {
  multiValue: true,
  render: ({ value, onChange, disabled }) => (
    <RangePicker
      style={{ width: '100%' }}
      value={value ?? null}
      disabled={disabled}
      onChange={onChange}
    />
  ),
});

// The experience "dragger" — one config field, two values.
registerFieldType('range', {
  multiValue: true,
  render: (props) => <RangeSliderField {...props} />,
});

// Preset dropdown (Today/Yesterday/.../Custom Range) that reveals a
// RangePicker for Custom Range. Not booleanEligible — the boolean-search API
// has no date parameter at all (Be_Auth_DevOps/models/boolean_search.go), so
// this field can never contribute to a boolean request in any mapping.
registerFieldType('daterangePreset', {
  multiValue: true,
  render: (props) => <DateRangePresetField {...props} />,
});
